import fs from "node:fs";
import path from "node:path";

import { stripCwdPrefix } from "../../../../shared/path-utils.js";
import type { ToolCallDetail } from "../../agent-sdk-types.js";
import { deriveToolDetail } from "../tool-detail-registry.js";

export type CodexToolDetailContext = {
  cwd?: string | null;
};

type CodexAttachmentLike = {
  mime?: unknown;
  mimeType?: unknown;
  url?: unknown;
};

function parseDataUrl(url: string): { mimeType: string; data: string } | null {
  if (!url.startsWith("data:")) return null;
  const commaIdx = url.indexOf(",");
  if (commaIdx === -1) return null;
  const header = url.slice(5, commaIdx);
  const semiIdx = header.indexOf(";");
  if (semiIdx === -1) return null;
  const mimeType = header.slice(0, semiIdx);
  const flags = header.slice(semiIdx + 1).split(";");
  if (!flags.includes("base64")) return null;
  const data = url.slice(commaIdx + 1);
  if (!mimeType || !data) return null;
  return { mimeType, data };
}

function extractImagesFromAttachments(
  attachments: unknown,
): Array<{ mimeType: string; data: string }> {
  if (!Array.isArray(attachments)) return [];
  const images: Array<{ mimeType: string; data: string }> = [];
  for (const raw of attachments) {
    if (!raw || typeof raw !== "object") continue;
    const attachment = raw as CodexAttachmentLike;
    const mime =
      typeof attachment.mime === "string"
        ? attachment.mime
        : typeof attachment.mimeType === "string"
          ? attachment.mimeType
          : null;
    if (!mime?.startsWith("image/") || typeof attachment.url !== "string") continue;
    const parsed = parseDataUrl(attachment.url);
    if (!parsed) continue;
    images.push(parsed);
  }
  return images;
}

function isPlaywrightScreenshotTool(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  return (
    normalized === "browser_take_screenshot" ||
    normalized === "mcp__playwright__browser_take_screenshot"
  );
}

function readStringField(value: unknown, field: string): string | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const raw = (value as Record<string, unknown>)[field];
  return typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : undefined;
}

function extractTextContent(output: unknown): string | undefined {
  if (typeof output === "string") return output;
  if (!output || typeof output !== "object" || Array.isArray(output)) return undefined;
  const record = output as Record<string, unknown>;
  if (typeof record.text === "string") return record.text;
  if (typeof record.output === "string") return record.output;
  if (typeof record.content === "string") return record.content;
  if (Array.isArray(record.content)) {
    const text = record.content
      .map((block) =>
        block &&
        typeof block === "object" &&
        typeof (block as Record<string, unknown>).text === "string"
          ? ((block as Record<string, unknown>).text as string)
          : "",
      )
      .filter((part) => part.length > 0)
      .join("\n");
    return text.length > 0 ? text : undefined;
  }
  return undefined;
}

function extractMarkdownScreenshotPath(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const match = text.match(/\[Screenshot[^\]]*\]\(([^)]+)\)/i);
  return match?.[1]?.trim() || undefined;
}

function mimeTypeForImagePath(filePath: string): string | undefined {
  switch (path.extname(filePath).toLowerCase()) {
    case ".jpg":
    case ".jpeg":
      return "image/jpeg";
    case ".png":
      return "image/png";
    case ".webp":
      return "image/webp";
    case ".gif":
      return "image/gif";
    default:
      return undefined;
  }
}

function resolveInsideCwd(cwd: string, candidate: string): string | undefined {
  const cwdReal = path.resolve(cwd);
  const resolved = path.isAbsolute(candidate)
    ? path.resolve(candidate)
    : path.resolve(cwdReal, candidate);
  if (resolved !== cwdReal && !resolved.startsWith(cwdReal + path.sep)) {
    return undefined;
  }
  return resolved;
}

function extractImageFromPlaywrightFile(params: {
  name: string;
  input: unknown;
  output: unknown;
  cwd?: string | null;
}): { mimeType: string; data: string } | null {
  if (!isPlaywrightScreenshotTool(params.name) || !params.cwd) return null;

  const explicitFilename = readStringField(params.input, "filename");
  const markdownPath = extractMarkdownScreenshotPath(extractTextContent(params.output));
  const candidates = [explicitFilename, markdownPath]
    .filter(
      (candidate): candidate is string => typeof candidate === "string" && candidate.length > 0,
    )
    .flatMap((candidate) => {
      if (path.isAbsolute(candidate)) {
        return [candidate];
      }
      const outputDirCandidate = path.join(
        ".appostle",
        "tmp",
        "playwright-screenshots",
        path.basename(candidate),
      );
      return candidate === outputDirCandidate ? [candidate] : [candidate, outputDirCandidate];
    });

  for (const candidate of candidates) {
    const resolved = resolveInsideCwd(params.cwd, candidate);
    if (!resolved) continue;
    const mimeType = mimeTypeForImagePath(resolved);
    if (!mimeType) continue;
    try {
      const data = fs.readFileSync(resolved).toString("base64");
      return { mimeType, data };
    } catch {
      continue;
    }
  }

  return null;
}

export function normalizeCodexFilePath(
  filePath: string,
  cwd: string | null | undefined,
): string | undefined {
  if (!filePath) {
    return undefined;
  }

  if (typeof cwd === "string" && cwd.length > 0) {
    return stripCwdPrefix(filePath, cwd);
  }
  return filePath;
}

function normalizePathForCwd(cwd: string | null): (filePath: string) => string | undefined {
  return (filePath) => normalizeCodexFilePath(filePath, cwd);
}

export function deriveCodexToolDetail(params: {
  name: string;
  input: unknown;
  output: unknown;
  attachments?: unknown;
  cwd?: string | null;
}): ToolCallDetail {
  const images = extractImagesFromAttachments(params.attachments);
  if (images.length > 0) {
    return { type: "image", toolName: params.name, images };
  }

  const playwrightImage = extractImageFromPlaywrightFile(params);
  if (playwrightImage) {
    return { type: "image", toolName: params.name, images: [playwrightImage] };
  }

  return deriveToolDetail(params.name, params.input, params.output, {
    normalizePath: params.cwd ? normalizePathForCwd(params.cwd) : undefined,
  });
}
